import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { getIdBadgeBorderColor } from '../../../utils/statusUtils';

export enum IdBadgeType {
  SKU = 'SKU',
  SPI = 'SPI',
  GPI = 'GPI',
  CPPI = 'CPPI',
  CPII = 'CPII',
  CPVI = 'CPVI',
}

interface IdBadgeProps {
  type: IdBadgeType;
  value: string | number;
  size?: 'sm' | 'md';
  showCopyIcon?: boolean;
  stopPropagation?: boolean;
  title?: string;
  truncate?: boolean;
}

export const IdBadge: React.FC<IdBadgeProps> = ({
  type,
  value,
  size = 'md',
  showCopyIcon = true,
  stopPropagation = false,
  title,
  truncate = false,
}) => {
  const [copied, setCopied] = useState(false);

  // Defensive: Ensure value is always a string or number, never an object
  const safeValue = React.useMemo(() => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    // If value is an object or invalid, convert to string
    console.error('IdBadge received invalid value:', value);
    return String(value);
  }, [value]);

  const handleCopy = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    navigator.clipboard.writeText(String(safeValue));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const displayValue =
    truncate && typeof safeValue === 'string' && safeValue.length > 6 ? `${safeValue.substring(0, 3)}xxx` : safeValue;

  const defaultTitle = title || `Click to copy ${type}`;

  const borderColor = getIdBadgeBorderColor(type);
  const fontSize = size === 'sm' ? '0.65rem' : '0.7rem';

  const badgeStyle = {
    fontSize,
    padding: '4px 8px',
    fontFamily: 'monospace',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `1px solid ${borderColor}`,
    color: borderColor,
    width: 'fit-content' as const,
  };

  if (showCopyIcon) {
    return (
      <div className="d-flex align-items-center">
        <span
          className="badge d-inline-flex align-items-center gap-1"
          style={badgeStyle}
          title={copied ? 'Copied!' : defaultTitle}
        >
          {type}: {displayValue}
          {copied ? (
            <Check size={size === 'sm' ? 10 : 12} style={{ color: '#28a745' }} />
          ) : (
            <Copy size={size === 'sm' ? 10 : 12} style={{ cursor: 'pointer' }} onClick={handleCopy} />
          )}
        </span>
      </div>
    );
  }

  return (
    <span className="badge" style={badgeStyle} onClick={handleCopy} title={copied ? 'Copied!' : defaultTitle}>
      {type}: {displayValue}
    </span>
  );
};
